/**
 * The Analysis tab of the Production Line detail page (production-lines):
 * loading, not started, in progress (the server's stages) or the findings
 * persisted under each completed step, with the bilingual step titles.
 * Moved verbatim out of ProductionLineDetailPage.tsx.
 */

import type { ReactNode } from 'react';

import type { MerchantProfileView } from '../../merchant/api.js';
import type { WorkspaceCopy } from '../../merchant/copy.js';
import type { IntakeJourney } from '../../merchant/data.js';
import { localizeMerchantFacingText } from '../../merchant/shared/merchantFacingText.js';
import {
  ButtonLink,
  Card,
  EmptyState,
  Eyebrow,
  Tide,
} from '../../merchant/ui/primitives.js';
import { AnalysisStages } from '../analysis-progress/AnalysisStages.js';
import type { Journey } from './productionLineJourney.js';
import { ProductionLineResearchResults } from './ProductionLineResearchResults.js';

export function ProductionLineAnalysisTab({
  copy,
  language,
  productionLineId,
  view,
  journey,
  journeyState,
}: {
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
  readonly productionLineId: string;
  readonly view: MerchantProfileView | null;
  readonly journey: Journey;
  readonly journeyState: IntakeJourney;
}): ReactNode {
  if (
    view?.result?.strategy === 'CONTENT_RESEARCH_V1' &&
    view.analysis.progress.overallStatus === 'completed'
  ) {
    return (
      <ProductionLineResearchResults
        view={view}
        language={language}
        copy={copy}
        lineId={productionLineId}
      />
    );
  }
  return (
    <div role="tabpanel" className="mj-ws-mt-20">
      {view === null || journey === 'loading' ? (
        <p className="mj-ws-loading" role="status">
          {copy.common.loading}
        </p>
      ) : journey === 'profile' || journey === 'checking' || journey === 'evidence' ? (
        <EmptyState
          title={copy.line.analysisNotStarted}
          body={copy.line.analysisNotStartedBody}
          action={
            <ButtonLink
              to={`/production-lines/${encodeURIComponent(productionLineId)}`}
              small
            >
              {copy.line.goToOverview}
            </ButtonLink>
          }
        />
      ) : view.analysis.progress.overallStatus !== 'completed' ? (
        <Card padding={24} style={{ maxWidth: 760 }}>
          <Eyebrow>
            <Tide />
            {copy.line.analysisInProgress}
          </Eyebrow>
          <AnalysisStages
            progress={view.analysis.progress}
            copy={copy}
            language={language}
            onRetry={journeyState.retryStep}
            working={journeyState.working}
          />
        </Card>
      ) : (
        <>
          <Eyebrow className="mj-ws-mt-12">{copy.line.whatFound}</Eyebrow>
          <div className="mj-ws-g3 mj-ws-mt-12">
            {view.analysis.progress.steps.map((step) => (
              <Card key={step.key} as="article" className="mj-ws-col">
                <h3 className="mj-ws-h3">{ANALYSIS_TITLE(step.key, language)}</h3>
                {(step.findings ?? []).length === 0 ? (
                  <p className="mj-ws-muted mj-ws-sm">{copy.line.noFindings}</p>
                ) : (
                  <ul className="mj-ws-sm mj-ws-checklist" style={{ margin: 0 }}>
                    {(step.findings ?? []).map((finding, index) => (
                      <li key={String(index)} style={{ marginTop: 4 }}>
                        {finding.summary}
                        {finding.limitation !== null ? (
                          <span className="mj-ws-finding-limitation">
                            {' '}
                            — {localizeMerchantFacingText(finding.limitation, language)}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            ))}
          </div>
          <p className="mj-ws-muted mj-ws-xs mj-ws-mt-16">{copy.line.publicOnly}</p>
        </>
      )}
    </div>
  );
}

function ANALYSIS_TITLE(key: string, language: 'en' | 'ar'): string {
  const titles: Record<string, { en: string; ar: string }> = {
    business: { en: 'Your business', ar: 'نشاطك' },
    market: { en: 'Your market', ar: 'سوقك' },
    competitors: { en: 'Public content', ar: 'المحتوى العام' },
    content: { en: 'Content reviewed', ar: 'المحتوى المُراجع' },
    patterns: { en: 'Patterns and signals', ar: 'الأنماط والإشارات' },
    recommendations: { en: 'Recommendations', ar: 'التوصيات' },
  };
  return titles[key]?.[language] ?? key;
}
